import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Briefcase, Plus, Search, Filter } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import api from '../utils/api';
import { toast } from 'sonner';
import { format } from 'date-fns';

const CasesPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchCases();
  }, []);

  const fetchCases = async () => {
    try {
      const response = await api.get('/cases');
      setCases(response.data);
    } catch (error) {
      toast.error(
        i18n.language === 'ar' ? 'فشل تحميل القضايا' : 'Failed to load cases'
      );
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'secondary',
      active: 'default',
      closed: 'outline',
      appealed: 'destructive',
    };
    return variants[status] || 'default';
  };

  const filteredCases = cases.filter(
    (c) =>
      c.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.case_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen" data-testid="cases-page">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 fade-in">
            <div>
              <h1 className="text-4xl font-bold mb-2">{t('myCases')}</h1>
              <p className="text-xl text-text-secondary">
                {i18n.language === 'ar'
                  ? `إجمالي ${cases.length} قضية`
                  : `Total ${cases.length} cases`
                }
              </p>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-8 fade-in" style={{ animationDelay: '0.1s' }}>
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
              <Input
                type="text"
                placeholder={i18n.language === 'ar' ? 'بحث في القضايا...' : 'Search cases...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 input"
                data-testid="search-cases-input"
              />
            </div>
          </div>

          {/* Cases Grid */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="spinner" />
            </div>
          ) : filteredCases.length === 0 ? (
            <Card className="card">
              <CardContent className="flex flex-col items-center justify-center py-20 text-center">
                <Briefcase className="w-16 h-16 text-text-secondary mb-4" />
                <p className="text-xl text-text-secondary">
                  {searchQuery
                    ? i18n.language === 'ar'
                      ? 'لم يتم العثور على نتائج'
                      : 'No results found'
                    : i18n.language === 'ar'
                    ? 'لا توجد قضايا'
                    : 'No cases yet'
                  }
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredCases.map((caseItem, index) => (
                <Card
                  key={caseItem.id}
                  className="card fade-in cursor-pointer"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onClick={() => navigate(`/cases/${caseItem.id}`)}
                  data-testid={`case-card-${index}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant={getStatusBadge(caseItem.status)} data-testid={`case-status-${index}`}>
                            {t(caseItem.status)}
                          </Badge>
                          <span className="text-sm text-text-secondary">{caseItem.case_number}</span>
                        </div>
                        <CardTitle className="text-xl">{caseItem.title}</CardTitle>
                      </div>
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center flex-shrink-0">
                        <Briefcase className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-text-secondary mb-4 line-clamp-2">{caseItem.description}</p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-text-secondary">
                        {i18n.language === 'ar' ? 'تاريخ الإنشاء:' : 'Created:'}
                      </span>
                      <span className="font-medium">
                        {format(new Date(caseItem.created_at), 'dd/MM/yyyy')}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default CasesPage;
